import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

/**
 * Reusable confirmation dialog component.
 * Displays a modal dialog with customizable title, description, and action buttons.
 *
 * @param props - Component props
 * @param props.isOpen - Whether the dialog is visible
 * @param props.onClose - Callback when dialog is closed (cancel)
 * @param props.onConfirm - Callback when user confirms the action
 * @param props.title - Dialog title (default: "Confirmare")
 * @param props.description - Dialog description/message (default: "Sunteți sigur că doriți să continuați?")
 * @param props.confirmText - Text for confirm button (default: "Confirmă")
 * @param props.cancelText - Text for cancel button (default: "Anulare")
 * @param props.variant - Button variant for confirm button (default: "default")
 * @param props.loading - Whether confirm button shows loading state (default: false)
 *
 * @example
 * ```tsx
 * <ConfirmationDialog
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onConfirm={handleDelete}
 *   title="Ștergere element"
 *   description="Sigur doriți să ștergeți acest element? Această acțiune nu poate fi anulată."
 *   confirmText="Șterge"
 *   cancelText="Anulare"
 *   variant="destructive"
 * />
 * ```
 */
export function ConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirmare',
  description = 'Sunteți sigur că doriți să continuați?',
  confirmText = 'Confirmă',
  cancelText = 'Anulare',
  variant = 'default',
  loading = false,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  loading?: boolean;
  /** Continut optional intre descriere si butoane (ex. un rezumat read-only). */
  children?: React.ReactNode;
}) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {children}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            {cancelText}
          </Button>
          <Button type="button" variant={variant} onClick={onConfirm} disabled={loading}>
            {loading ? 'Se procesează...' : confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
